import type { ResponseOf, RouteDef } from '@pmai/contracts';
import { useEffect } from 'react';
import type { DefaultValues, FieldValues } from 'react-hook-form';
import type { z } from 'zod';
import { useApiMutation } from '../api/hooks';
import { useErrorText } from '../i18n';
import { applyFieldErrors, useZodForm } from './form';

/**
 * EntityForm (engineering.md §3) — what every create/edit dialog shares: one form on the API's
 * zod schema, create and update mutations that refresh the given lists and put server field
 * errors back on the fields, and a reset to the record's (or the empty) values each time the
 * dialog opens. A dialog adds only its fields and how a submit maps to the routes.
 */
export function useEntityForm<
  S extends z.ZodType<FieldValues, FieldValues>,
  C extends RouteDef,
  U extends RouteDef,
>(opts: {
  schema: S;
  open: boolean;
  /** The record being edited, or null for a new one; a different record resets the form. */
  record: object | null;
  /** Values the form opens with: the record's, or the empty form. */
  values: DefaultValues<z.input<S>>;
  create: C;
  update: U;
  invalidates: readonly RouteDef[];
  onClose: () => void;
  /** Called with the saved record before the dialog closes. */
  onSaved?: (saved: ResponseOf<C> | ResponseOf<U>) => void;
  /** Toast keys after a create / an update. */
  success?: { create?: string; update?: string };
}) {
  const errorText = useErrorText();
  const form = useZodForm(opts.schema, { defaultValues: opts.values });
  const onError = (error: unknown) => applyFieldErrors(form, error);
  const done = (saved: ResponseOf<C> | ResponseOf<U>) => {
    opts.onSaved?.(saved);
    opts.onClose();
  };
  const common = { invalidates: opts.invalidates, onError };
  const create = useApiMutation(opts.create, { ...common, onSuccess: done, success: opts.success?.create });
  const update = useApiMutation(opts.update, { ...common, onSuccess: done, success: opts.success?.update });
  const active = opts.record ? update : create;
  const { reset } = form;

  useEffect(() => {
    if (!opts.open) return;
    create.reset();
    update.reset();
    reset(opts.values);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the dialog opens (for a record)
  }, [opts.open, opts.record, reset]);

  return {
    form,
    create,
    update,
    /** Spread onto `<FormDialog>`. */
    dialog: {
      open: opts.open,
      onOpenChange: (open: boolean) => !open && opts.onClose(),
      submitting: active.isPending,
      error: active.error ? errorText(active.error) : null,
    },
  };
}
